import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, Calendar, Clock, AlertTriangle, Check, X, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import mfaService from '../../services/mfaService';
import { AuthContext } from '../../contexts/AuthContext';
import hasUserRole from '../../utils/UserUtil';

const StudyMFASettings = ({ studyId }) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mfaSettings, setMfaSettings] = useState({
    mfa_required_for_all_users: false,
    mfa_enforcement_date: '',
    grace_period_days: 30,
    users: []
  });
  const [originalSettings, setOriginalSettings] = useState(null);

  // Check if user can manage MFA settings
  const canManageMFA = hasUserRole(user, ['Program Admin', 'Study Coordinator', 'Investigator']) || user?.is_superuser;

  useEffect(() => {
    loadMFASettings();
  }, [studyId]);

  const loadMFASettings = async () => {
    try {
      setLoading(true);
      const data = await mfaService.getStudyMFASettings(studyId);
      setMfaSettings(data);
      setOriginalSettings(data);
    } catch (error) {
      console.error('Error loading MFA settings:', error);
      if (error.response?.status === 404) {
        // No MFA settings exist yet, use defaults
        const defaultSettings = {
          mfa_required_for_all_users: false,
          mfa_enforcement_date: '',
          grace_period_days: 30,
          users: []
        };
        setMfaSettings(defaultSettings);
        setOriginalSettings(defaultSettings);
      } else {
        toast.error('Failed to load MFA settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = (field, value) => {
    setMfaSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const settingsToSave = {
        mfa_required_for_all_users: mfaSettings.mfa_required_for_all_users,
        mfa_enforcement_date: mfaSettings.mfa_enforcement_date || null,
        grace_period_days: mfaSettings.grace_period_days
      };
      
      const updatedSettings = await mfaService.updateStudyMFASettings(studyId, settingsToSave);
      setMfaSettings(updatedSettings);
      setOriginalSettings(updatedSettings);
      toast.success('MFA settings updated successfully');
    } catch (error) {
      console.error('Error saving MFA settings:', error);
      toast.error('Failed to save MFA settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUserMFAOverride = async (userId, override) => {
    try {
      await mfaService.overrideUserMFA(studyId, userId, { mfa_required: override });
      toast.success(`User MFA requirement ${override ? 'enabled' : 'disabled'}`);
      // Reload settings to get updated user list
      loadMFASettings();
    } catch (error) {
      console.error('Error overriding user MFA:', error);
      toast.error('Failed to update user MFA requirement');
    }
  };

  const hasUnsavedChanges = () => {
    if (!originalSettings) return false;
    return (
      mfaSettings.mfa_required_for_all_users !== originalSettings.mfa_required_for_all_users ||
      mfaSettings.mfa_enforcement_date !== originalSettings.mfa_enforcement_date ||
      mfaSettings.grace_period_days !== originalSettings.grace_period_days
    );
  };

  const getEnforcementStatus = () => {
    if (!mfaSettings.mfa_required_for_all_users) {
      return { status: 'disabled', text: 'MFA is not required for this study', color: 'text-gray-600' };
    }
    
    if (!mfaSettings.mfa_enforcement_date) {
      return { status: 'active', text: 'MFA is required immediately', color: 'text-green-600' };
    }
    
    const enforcementDate = new Date(mfaSettings.mfa_enforcement_date);
    const now = new Date();
    
    if (enforcementDate > now) {
      const daysUntil = Math.ceil((enforcementDate - now) / (1000 * 60 * 60 * 24));
      return { 
        status: 'pending', 
        text: `MFA enforcement starts in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`, 
        color: 'text-yellow-600' 
      };
    }
    
    return { status: 'active', text: 'MFA enforcement is active', color: 'text-green-600' };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!canManageMFA) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
          <p className="text-yellow-800">
            You don't have permission to manage MFA settings for this study.
          </p>
        </div>
      </div>
    );
  }

  const enforcementStatus = getEnforcementStatus();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Status Overview */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Shield className="w-5 h-5 mr-2 text-primary-600" />
            MFA Status Overview
          </h3>
          <div className={`flex items-center ${enforcementStatus.color}`}>
            <div className={`w-2 h-2 rounded-full mr-2 ${
              enforcementStatus.status === 'active' ? 'bg-green-500' :
              enforcementStatus.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-400'
            }`}></div>
            {enforcementStatus.text}
          </div>
        </div>
        
        {mfaSettings.users && mfaSettings.users.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">
                {mfaSettings.users.length}
              </div>
              <div className="text-sm text-gray-600">Total Users</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {mfaSettings.users.filter(u => u.mfa_setup_completed).length}
              </div>
              <div className="text-sm text-gray-600">MFA Configured</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-red-600">
                {mfaSettings.users.filter(u => u.mfa_required && !u.mfa_setup_completed).length}
              </div>
              <div className="text-sm text-gray-600">Pending Setup</div>
            </div>
          </div>
        )}
      </div>

      {/* MFA Settings */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2 text-primary-600" />
          MFA Configuration
        </h3>
        
        <div className="space-y-6">
          {/* Enable MFA Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Require MFA for all study users
              </label>
              <p className="text-sm text-gray-500">
                When enabled, all users associated with this study must set up and use MFA
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={mfaSettings.mfa_required_for_all_users}
                onChange={(e) => handleSettingsChange('mfa_required_for_all_users', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {/* Enforcement Date */}
          {mfaSettings.mfa_required_for_all_users && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Enforcement Date (Optional)
              </label>
              <input
                type="date"
                value={mfaSettings.mfa_enforcement_date}
                onChange={(e) => handleSettingsChange('mfa_enforcement_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Leave empty to require MFA immediately. If set, users will have until this date to set up MFA.
              </p>
            </div>
          )}

          {/* Grace Period */}
          {mfaSettings.mfa_required_for_all_users && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Grace Period (Days)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={mfaSettings.grace_period_days}
                onChange={(e) => handleSettingsChange('grace_period_days', parseInt(e.target.value) || 30)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Number of days users have to set up MFA after the requirement becomes active.
              </p>
            </div>
          )}

          {/* Save Button */}
          {hasUnsavedChanges() && (
            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* User Management */}
      {mfaSettings.users && mfaSettings.users.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-primary-600" />
            User MFA Status
          </h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MFA Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Required
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                {mfaSettings.users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.role_in_study}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        (user.mfa_status?.mfa_setup_completed || user.mfa_status?.is_mfa_enabled)
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.mfa_status?.mfa_setup_completed || user.mfa_status?.is_mfa_enabled ? (
                          <>
                            <Check className="w-3 h-3 mr-1" />
                            Configured
                          </>
                        ) : (
                          <>
                            <X className="w-3 h-3 mr-1" />
                            Not Set Up
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        (user.mfa_status?.mfa_required === true || mfaSettings.mfa_required_for_all_users === true)
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {(user.mfa_status?.mfa_required === true || mfaSettings.mfa_required_for_all_users === true) ? 'Required' : 'Optional'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {/* Hide "Make Optional" button when MFA is required (either by study policy or individual requirement) */}
                      {!(user.mfa_status?.mfa_required === true || mfaSettings.mfa_required_for_all_users === true) && (
                        <button
                          onClick={() => handleUserMFAOverride(user.user_id, !user.mfa_status?.mfa_required)}
                          className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 text-sm px-3 py-1 rounded-md"
                        >
                          Require MFA
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default StudyMFASettings;
